import {
    ExtendedConstructionNumber,
    ExtendedProjectPhase,
    hasDatePassed,
} from '@bpd-library/utilities';
import { UserBuyerStatus } from '@utilities/api-calls/buyer-status';
import { translateItem } from '@utilities/hooks';
import { isNetherlands } from '@utilities/is-netherlands';

export type HouseDetailpageCtaAction =
    | 'requestLeadFormNetherlands'
    | 'requestLeadFormGermany'
    | 'registerPreference'
    | 'requestOption'
    | 'becomeReserveCandidate'
    | 'returnToOverview'
    | 'signup';

export type HouseDetailpageCtaFlow = 'subscription' | 'vkk' | 'overview';

/**
 * Logic ported from `ProjectExtensions.cs`
 *
 * @param projectPhase - *ExtendedProjectPhase*
 * @param constructionNumber *ExtendedConstructionNumber*
 * @returns *HouseDetailpageCtaAction | undefined*
 */
const ctaVkkOptionAction = (
    projectPhase: ExtendedProjectPhase,
    constructionNumber?: ExtendedConstructionNumber,
): HouseDetailpageCtaAction | undefined => {
    const { status: projectPhaseStatus, allocationDate, saleStartDate, saleEndDate } = projectPhase;
    const { availabilityStatus: constructionNumberStatus, numberOfReserveCandidates } =
        constructionNumber || {};

    if (projectPhaseStatus !== 'OnSale' || (saleStartDate && !hasDatePassed(saleStartDate))) return;

    switch (constructionNumberStatus) {
        case 'Available': {
            if (saleEndDate && !hasDatePassed(saleEndDate)) {
                return 'registerPreference';
            } else if (
                (allocationDate && !hasDatePassed(allocationDate)) ||
                (numberOfReserveCandidates && numberOfReserveCandidates > 0)
            ) {
                return 'becomeReserveCandidate';
            } else {
                return 'requestOption';
            }
        }

        case 'InOption': {
            if (saleEndDate && hasDatePassed(saleEndDate)) {
                return 'becomeReserveCandidate';
            }
        }
    }

    return;
};

const ctaActionNetherlands = (
    projectPhase: ExtendedProjectPhase,
    constructionNumber?: ExtendedConstructionNumber,
    startSaleUrl?: string | null,
): HouseDetailpageCtaAction | undefined => {
    const { status: projectPhaseStatus } = projectPhase;
    const { status: constructionNumberStatus } = constructionNumber || {};

    if (!constructionNumber) {
        switch (projectPhaseStatus) {
            case 'InPreparation':
                return 'requestLeadFormNetherlands';

            case 'OnSale':
            case 'ForRent':
                return 'returnToOverview';
        }
    } else {
        switch (projectPhaseStatus) {
            case 'InPreparation':
                return 'requestLeadFormNetherlands';

            case 'OnSale':
            case 'ForRent': {
                switch (constructionNumberStatus) {
                    case 'AssigningOn':
                    case 'Available':
                    case 'InOption': {
                        const action = ctaVkkOptionAction(projectPhase, constructionNumber);

                        if (action) return action;
                        else if (startSaleUrl) return 'signup';
                        else return 'requestLeadFormNetherlands';
                    }

                    case 'Rented':
                    case 'Sold':
                    case 'Reserved':
                        return 'returnToOverview';
                }
                return;
            }

            case 'Rented':
            case 'Sold':
                return 'returnToOverview';
        }
    }

    return;
};

const ctaActionGermany = (
    projectPhase: ExtendedProjectPhase,
    constructionNumber?: ExtendedConstructionNumber,
): HouseDetailpageCtaAction | undefined => {
    const { status: projectPhaseStatus } = projectPhase;
    const { status: constructionNumberStatus } = constructionNumber || {};

    if (
        ['InPreparation', 'OnSale'].includes(projectPhaseStatus || '') &&
        constructionNumberStatus !== 'Sold'
    ) {
        return 'requestLeadFormGermany';
    }
    return 'returnToOverview';
};

const getCtaFlow = (
    projectPhase: ExtendedProjectPhase,
    constructionNumber?: ExtendedConstructionNumber,
    startSaleUrl?: string | null,
): { action: HouseDetailpageCtaAction; flow: HouseDetailpageCtaFlow } | undefined => {
    const action = isNetherlands()
        ? ctaActionNetherlands(projectPhase, constructionNumber, startSaleUrl)
        : ctaActionGermany(projectPhase, constructionNumber);

    switch (action) {
        case 'requestLeadFormGermany':
        case 'requestLeadFormNetherlands':
            return { action, flow: 'subscription' };
        case 'registerPreference':
        case 'becomeReserveCandidate':
        case 'requestOption':
        case 'signup':
            return { action, flow: 'vkk' };
        case 'returnToOverview':
            return { action, flow: 'overview' };
    }

    return;
};

const ctaLabelForAction = (
    action: HouseDetailpageCtaAction,
    completed?: UserBuyerStatus | boolean,
) => {
    let labelKey = '';
    switch (completed) {
        case 'hasPreference':
            labelKey = CTA_LABEL_DICTIONARY_KEY.registerPreference.completed!;
            break;
        case 'isReserveCandidate':
            labelKey = CTA_LABEL_DICTIONARY_KEY.becomeReserveCandidate.completed!;
            break;
        case 'hasOption':
            labelKey = CTA_LABEL_DICTIONARY_KEY.requestOption.completed!;
            break;
        default:
            labelKey = CTA_LABEL_DICTIONARY_KEY[action][completed ? 'completed' : 'initial'] || '';
            break;
    }
    return translateItem(labelKey);
};

const CTA_LABEL_DICTIONARY_KEY: Record<
    HouseDetailpageCtaAction,
    { initial: string; completed?: string }
> = {
    requestLeadFormNetherlands: {
        initial: 'cta_RegisterForProjectPhase',
        completed: 'cta_SubscribedToProjectPhase',
    },
    requestLeadFormGermany: {
        initial: 'cta_IAmInterested',
        completed: 'cta_ShownInterest',
    },
    registerPreference: {
        initial: 'cta_GivePreference',
        completed: 'cta_HasPreference',
    },
    requestOption: {
        initial: 'cta_RequestOption',
        completed: 'cta_HasOption',
    },
    becomeReserveCandidate: {
        initial: 'cta_WaitingList',
        completed: 'cta_IsReserveCandidate',
    },
    returnToOverview: {
        initial: 'cta_ViewAvailableRange',
    },
    signup: {
        initial: 'cta_Signup',
    },
};

export { getCtaFlow, ctaLabelForAction };
